import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [phoneVerificationInProgress, setPhoneVerificationInProgress] = useState(false)
  const [verificationPhoneNumber, setVerificationPhoneNumber] = useState('')

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        setLoading(true)
        
        // Get current session
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          throw error
        }
        
        setSession(session)
        setUser(session?.user || null)
      } catch (error) {
        console.error('Error getting session:', error.message)
        setError(error.message)
      } finally {
        setLoading(false)
      }
    }
    
    getInitialSession()
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user || null)
      setLoading(false)
    })

    return () => {
      subscription?.unsubscribe()
    }
  }, [])

  // Sign up a new user with email
  const signUp = async (email, password) => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })
      
      if (error) throw error
      
      return data
    } catch (error) {
      console.error('Error signing up:', error.message)
      setError(error.message)
      return { error }
    } finally {
      setLoading(false)
    }
  }

  // Sign in an existing user with email
  const signIn = async (email, password) => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) throw error
      
      return data
    } catch (error) {
      console.error('Error signing in:', error.message)
      setError(error.message)
      return { error }
    } finally {
      setLoading(false)
    }
  }

  // Initiate phone number sign-in/sign-up
  const initiatePhoneAuth = async (phoneNumber) => {
    try {
      setLoading(true)
      setError(null)
      
      // Set verification phone number and progress state before API call
      setVerificationPhoneNumber(phoneNumber)
      setPhoneVerificationInProgress(true)
      
      const { data, error } = await supabase.auth.signInWithOtp({
        phone: phoneNumber,
      })
      
      if (error) {
        // Reset verification state on error
        setPhoneVerificationInProgress(false)
        throw error
      }
      
      return data
    } catch (error) {
      console.error('Error initiating phone auth:', error.message)
      setError(error.message)
      return { error }
    } finally {
      setLoading(false)
    }
  }

  // Verify phone auth OTP
  const verifyPhoneOtp = async (otp) => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await supabase.auth.verifyOtp({
        phone: verificationPhoneNumber,
        token: otp,
        type: 'sms'
      })
      
      if (error) throw error
      
      // Reset the verification state
      setPhoneVerificationInProgress(false)
      setVerificationPhoneNumber('')
      
      return data
    } catch (error) {
      console.error('Error verifying OTP:', error.message)
      setError(error.message)
      return { error }
    } finally {
      setLoading(false)
    }
  }

  // Cancel phone verification
  const cancelPhoneVerification = () => {
    setPhoneVerificationInProgress(false)
    setVerificationPhoneNumber('')
  }

  // Sign out the current user
  const signOut = async () => {
    try {
      setLoading(true)
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (error) {
      console.error('Error signing out:', error.message)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const value = {
    user,
    session,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    initiatePhoneAuth,
    verifyPhoneOtp,
    phoneVerificationInProgress,
    verificationPhoneNumber,
    cancelPhoneVerification
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 